<template>
  <v-dialog :model-value="modelValue" fullscreen persistent transition="dialog-bottom-transition">
    <!-- Seaded -> Dialoogid uues kujunduses: whose signature, for what, and a
         line to sign on. The same pad, Loobu / Tühjenda / Valmis as before. -->
    <div v-if="plate" class="dlg dlg--sign" role="dialog" :aria-label="title">
      <div class="dlg-head">
        <div>
          <div v-if="caption" class="dlg-cap">{{ caption }}</div>
          <h2 class="dlg-title">{{ title }}</h2>
        </div>
        <button type="button" class="dlg-x" aria-label="Sulge" @click="cancel"><v-icon>mdi-close</v-icon></button>
      </div>
      <div class="dlg-sign-meta"><span>Allkirjasta sõrme või hiirega joonele.</span><span>Telefonis keera ekraan laiaks.</span></div>
      <div class="dlg-sign-pad">
        <span class="dlg-sign-x" aria-hidden="true">✕</span>
        <span class="dlg-sign-line" aria-hidden="true" />
        <signature-pad
          v-if="modelValue"
          ref="pad"
          hide-bar
          :height="padHeight"
          @change="drawn = $event"
        />
      </div>
      <div class="dlg-foot" style="margin-top: 12px">
        <button type="button" class="dlg-btn dlg-btn--ghost" @click="cancel">Loobu</button>
        <span class="dlg-grow" />
        <button type="button" class="dlg-btn dlg-btn--ghost" @click="clear">Tühjenda</button>
        <button type="button" class="dlg-btn" :disabled="!drawn" @click="done"><v-icon size="18">mdi-check</v-icon>Valmis</button>
      </div>
    </div>
    <v-card v-else class="d-flex flex-column signature-dialog">
      <v-card-title class="text-subtitle-1">{{ title }}</v-card-title>
      <v-card-text class="flex-grow-1 pb-0">
        <signature-pad
          v-if="modelValue"
          ref="pad"
          hide-bar
          :height="padHeight"
          @change="drawn = $event"
        />
      </v-card-text>
      <v-card-actions>
        <v-btn variant="text" @click="cancel">Loobu</v-btn>
        <v-spacer />
        <v-btn variant="text" class="mr-2" @click="clear">Tühjenda</v-btn>
        <v-btn color="green-darken-3"  :disabled="!drawn" @click="done">
          <v-icon start>mdi-check</v-icon>Valmis
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
// A signature is drawn with a finger, so it gets the whole screen rather than a
// 160px strip inside a form. Loobu throws the drawing away, Tühjenda starts it
// over, Valmis hands the data URL to the parent.
//
// v-model is the open state; `initial` is the signature to start from, so
// reopening shows what is already on the act and lets it be corrected instead of
// forcing a redraw.
import { mapState } from 'vuex'
import '@/styles/dialog-plate.css'

export default {
  emits: ['update:modelValue', 'save'],
  props: {
    modelValue: { type: Boolean, default: false },
    title: { type: String, default: 'Allkiri' },
    // The new look's line above the title: what the signature is for.
    caption: { type: String, default: '' },
    initial: { type: String, default: null }
  },
  data () {
    return {
      drawn: false,
      padHeight: 320
    }
  },
  computed: {
    ...mapState(['features']),
    plate () {
      return !!(this.features && this.features.dialogPlate)
    }
  },
  watch: {
    modelValue (open) {
      if (!open) return
      this.measure()
      // The pad is inside a dialog that was not on screen a moment ago, so its
      // canvas has no width yet and resize() on mount was a no-op. rAF after the
      // tick is the first point the laid-out width is readable.
      this.$nextTick(() => window.requestAnimationFrame(() => {
        this.drawn = false
        if (this.$refs.pad) this.$refs.pad.reset(this.initial)
      }))
    }
  },
  mounted () {
    this.measure()
    window.addEventListener('resize', this.measure)
  },
  beforeUnmount () {
    window.removeEventListener('resize', this.measure)
  },
  methods: {
    // Leave room for the title and the action bar, and stay sane on both a phone
    // in landscape and a tall desktop window.
    measure () {
      // The new look has a hint line above the pad as well.
      const available = (window.innerHeight || 600) - (this.plate ? 220 : 180)
      this.padHeight = Math.max(220, Math.min(520, available))
    },
    clear () {
      if (this.$refs.pad) this.$refs.pad.clear()
    },
    cancel () {
      this.$emit('update:modelValue', false)
    },
    done () {
      // Capture before closing: the dialog unmounting takes the canvas with it.
      const dataUrl = this.$refs.pad ? this.$refs.pad.toDataURL() : null
      this.$emit('save', dataUrl)
      this.$emit('update:modelValue', false)
    }
  }
}
</script>

<style scoped>
/* Fullscreen v-dialog gives the card the viewport height; the pad claims what is
   left between the title and the actions. */
.signature-dialog {
  min-height: 100%;
}
</style>
